import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  Chip,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  Close,
  LinkedIn,
  Download,
  Share,
  CheckCircle,
  EmojiEvents,
  Verified
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.theskillgenie.com';

/**
 * CERTIFICATE CONGRATULATIONS MODAL
 * Auto-shows when student generates certificate (100% completion)
 * Encourages immediate LinkedIn sharing with "Skill Genie Verified" branding
 */
const CertificateCongratsModal = ({ open, onClose, certificate }) => {
  const [shareTracked, setShareTracked] = useState(false);
  const [downloading, setDownloading] = useState(false);

  if (!certificate) return null;

  const handleShareLinkedIn = async () => {
    // Track share
    try {
      if (!shareTracked) {
        await axios.post(
          `${API_BASE_URL}/api/certificates/${certificate.secureToken}/share`,
          {
            platform: 'LINKEDIN',
            sharedBy: certificate.studentId
          }
        );
        setShareTracked(true);
      }
    } catch (err) {
      console.error('Failed to track share:', err);
    }

    // Open LinkedIn share with custom message
    const shareText = encodeURIComponent(
      `🎉 Excited to share that I've completed ${certificate.programName} and earned my Skill Genie Verified certification! 💪\n\n✅ 110 tasks completed\n✅ Hands-on projects\n✅ Verified by ${certificate.collegeName}\n\nCheck out my proof of work: ${certificate.verificationUrl}\n\n#SkillGenieVerified #TechCertification #ProofOfWork`
    );

    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(certificate.verificationUrl)}&summary=${shareText}`;
    window.open(linkedInUrl, '_blank', 'width=600,height=600');
  };

  const handleAddToProfile = () => {
    const linkedInUrl = `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${encodeURIComponent(certificate.programName)}&organizationName=EvolveX%20Innovations&issueYear=${new Date(certificate.issueDate).getFullYear()}&issueMonth=${new Date(certificate.issueDate).getMonth() + 1}&certUrl=${encodeURIComponent(certificate.verificationUrl)}&certId=${certificate.certificateNumber}`;

    window.open(linkedInUrl, '_blank', 'width=600,height=600');
  };

  const handleDownload = async () => {
    if (!certificate.secureToken && !certificate.certificatePdfUrl) return;
    setDownloading(true);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/certificates/download/${encodeURIComponent(certificate.secureToken)}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      const downloadUrl = response.data?.downloadUrl;
      if (!downloadUrl) {
        throw new Error('No download URL returned from server');
      }

      const fileResponse = await fetch(downloadUrl);
      if (!fileResponse.ok) {
        throw new Error('Failed to fetch certificate PDF');
      }

      const blob = await fileResponse.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = response.data.fileName || `${certificate.certificateNumber || 'certificate'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Download failed:', err);
      if (certificate.certificatePdfUrl) {
        window.open(certificate.certificatePdfUrl, '_blank', 'noopener,noreferrer');
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleViewVerification = () => {
    if (certificate.secureToken) {
      window.open(
        `${window.location.origin}/verify/${encodeURIComponent(certificate.secureToken)}`,
        '_blank',
        'noopener,noreferrer'
      );
      return;
    }

    if (certificate.verificationUrl) {
      window.open(certificate.verificationUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden'
        }
      }}
    >
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          p: 2,
          position: 'relative'
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'white'
          }}
        >
          <Close />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 4, textAlign: 'center' }}>
        {/* Trophy Animation */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 2,
            animation: 'bounce 1s ease infinite'
          }}
        >
          <EmojiEvents sx={{ fontSize: 80, color: '#FFD700' }} />
        </Box>

        <Typography
          variant="h4"
          fontWeight="bold"
          gutterBottom
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
        >
          🎉 Congratulations! 🎉
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h6" color="text.secondary">
            You are now
          </Typography>
          <Chip
            icon={<Verified />}
            label="Skill Genie Verified"
            sx={{
              backgroundColor: '#1F8ACB',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '1rem',
              px: 1
            }}
          />
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Show recruiters your <strong>Proof of Work</strong> and stand out from the crowd!
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Certificate Summary Card */}
        <Card sx={{ mb: 3, backgroundColor: '#f5f7fa', boxShadow: 'none' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
              <CheckCircle color="success" />
              <Typography variant="h6" fontWeight="bold">
                {certificate.programName}
              </Typography>
            </Box>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={4}>
                <Typography variant="body2" color="text.secondary">
                  Certificate ID
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {certificate.certificateNumber}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2" color="text.secondary">
                  Grade
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="success.main">
                  {certificate.overallGrade}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2" color="text.secondary">
                  Tasks Completed
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {certificate.completedTasks}/{certificate.totalTasks}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Call to Action */}
        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
          📢 Share Your Achievement
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Your public verification page includes:
        </Typography>

        <Box sx={{ textAlign: 'left', mb: 3, maxWidth: 400, mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CheckCircle color="success" fontSize="small" />
            <Typography variant="body2">Verified by EvolveX badge</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CheckCircle color="success" fontSize="small" />
            <Typography variant="body2">Complete proof of work (110 tasks)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CheckCircle color="success" fontSize="small" />
            <Typography variant="body2">GitHub commits and contributions</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CheckCircle color="success" fontSize="small" />
            <Typography variant="body2">HOD approval verification</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle color="success" fontSize="small" />
            <Typography variant="body2">Rich preview on LinkedIn</Typography>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<LinkedIn />}
            onClick={handleShareLinkedIn}
            sx={{
              backgroundColor: '#0077B5',
              '&:hover': { backgroundColor: '#005885' },
              py: 1.5,
              fontWeight: 'bold',
              textTransform: 'none',
              fontSize: '1rem'
            }}
          >
            Share on LinkedIn Feed
          </Button>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<LinkedIn />}
              onClick={handleAddToProfile}
              sx={{ textTransform: 'none' }}
            >
              Add to LinkedIn Profile
            </Button>

            <Button
              variant="outlined"
              fullWidth
              startIcon={<Download />}
              onClick={handleDownload}
              disabled={downloading}
              sx={{ textTransform: 'none' }}
            >
              {downloading ? 'Downloading…' : 'Download PDF'}
            </Button>
          </Box>

          <Button
            variant="text"
            fullWidth
            startIcon={<Share />}
            onClick={handleViewVerification}
            sx={{ textTransform: 'none' }}
          >
            View Verification Page
          </Button>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
          💡 Pro Tip: Sharing on LinkedIn increases your profile visibility by 10x!
        </Typography>
      </DialogContent>

      {/* CSS Animation */}
      <style>{`
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
      `}</style>
    </Dialog>
  );
};

export default CertificateCongratsModal;
